<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from "vue";
import { useGlobalStore } from "@stores/global";
import { useGlobeStore } from "@stores/globe";
import { useDataStore } from "@stores/data";
import { useAchievementsStore } from "@stores/achievements";
import VoteSidebar from "@components/vote/Sidebar.vue";
import VoteCountryInfo from "@components/vote/CountryInfo.vue";

const globalStore = useGlobalStore();
const globeStore = useGlobeStore();
const dataStore = useDataStore();
const achievementsStore = useAchievementsStore();

const processing = ref(false);
const errors = ref({});
const voteData = ref({ name: "", ends_at: null, votes: [] });
const voteLoading = ref(true);

const vote = ref({
    vote_window_id: "",
    country: "",
    email: "",
    agreed: false,
});
const introClosed = ref(false);

watch(
    () => globalStore.modal.name,
    (name) => {
        if (name !== "votingIntro") introClosed.value = true;
    },
);

const countryPlaceholder = computed(() => {
    const hovered = globeStore.hoveredCountry;
    if (!hovered) return "";
    const city = dataStore.searchableCities.find((c) => c.name === hovered);
    return city?.countryName ?? hovered;
});

watch(
    () => globeStore.selectedCountry,
    (cityName) => {
        if (!cityName) return;
        const city = dataStore.searchableCities.find(
            (c) => c.name === cityName,
        );
        vote.value.country = city?.countryName ?? cityName;
    },
);

watch(
    () => voteData.value,
    (data) => {
        vote.value.vote_window_id = data.id;
    },
);

onUnmounted(() => {
    globeStore.clearSelection();
});

function handleSubmit() {
    if (!processing.value) {
        processing.value = true;
        axios
            .post("/api/vote", vote.value)
            .then((r) => {
                achievementsStore.incrementProgress("democracy", 1);
                globalStore.openModal("votingSuccess");
                if (typeof window.gtag === "function") {
                    window.gtag("event", "submitted_country_vote", {
                        country_name: vote.value.country,
                    });
                }
            })
            .catch((error) => {
                processing.value = false;
                if (error.response.status === 422) {
                    errors.value = error.response.data.errors;
                    console.error(errors.value);
                }
            });
    }
}

onMounted(async () => {
    globalStore.openModal("votingIntro", { fullHeight: true });
    try {
        const { data } = await axios.get("/api/vote");
        voteData.value = data;
    } finally {
        voteLoading.value = false;
    }
});
</script>

<template>
    <div class="contents">
        <VoteCountryInfo v-if="introClosed" />
        <VoteSidebar
            v-if="introClosed"
            :data="voteData"
            :loading="voteLoading"
            :errors="errors"
            :country-placeholder="countryPlaceholder"
            v-model:country="vote.country"
            v-model:email="vote.email"
            v-model:agreed="vote.agreed"
            @submit="handleSubmit"
        />
    </div>
</template>
